import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';
import { zodResolver } from '@hookform/resolvers/zod';

import dayjs from 'dayjs';
import { z } from 'zod';

import { usePostCreateTransferInfoApi, usePostPaymentReadyApi } from '@/api/payment';
import { ModalPrimaryButton as PaymentConfirmButton, ModalSecondaryButton as CancelButton } from '@/components/common/Modal/modalButtonStyles';
import MoneyIcon from '@/components/Icon/MoneyIcon';
import MoneyIconActive from '@/components/Icon/MoneyIconActive';
import { CONF_TITLE } from '@/constants/menu';
import {
	GoodsName,
	PAYMENT_ANNUAL_FEE_YEAR_SUFFIX,
	PAYMENT_CONFERENCE_POSITION_GRADUATE,
	PAYMENT_CONFERENCE_POSITION_PROFESSOR,
	PAYMENT_CONFERENCE_POSITION_UNDERGRADUATE,
	PAYMENT_GOODS_ANNUAL_FEE,
	PAYMENT_GOODS_CONFERENCE_FEE,
	PAYMENT_GOODS_PUBLICATION_FEE,
	PAYMENT_MEMBER_BOARD,
	PAYMENT_MEMBER_LIFETIME,
	PAYMENT_MEMBER_REGULAR,
	PAYMENT_MEMBER_STUDENT,
	PAYMENT_MEMBER_TYPES,
	PAYMENT_METHOD_ACCOUNT,
	PAYMENT_METHOD_CARD,
	PAYMENT_PUBLICATION_TYPE_BASIC,
	PAYMENT_PUBLICATION_TYPE_EXPRESS,
} from '@/constants/payment';
import { convertConferencePaymentValue, convertPaymentValue, convertPublicationPaymentValue } from '@/utils/convertValue';

import Field from '../../Field/Field';

interface RegisterField {
	payment: string;
	member: string;
	memberPosition: string;
	publicationType: string;
}

interface PaymentModalProps {
	closeModal: (bool: boolean) => void;
	goodsName: GoodsName;
	memberType?: string;
}
const PaymentModal = ({ closeModal, goodsName, memberType }: PaymentModalProps) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const year = dayjs().format('YYYY');
	const isAnnualFee = goodsName === PAYMENT_GOODS_ANNUAL_FEE;
	const isConferenceFee = goodsName === PAYMENT_GOODS_CONFERENCE_FEE;
	const userRegisterSchema = z.object({
		payment: z.string(),
		member: z.string(),
		memberPosition: z.string(),
		publicationType: z.string(),
	});

	const conferencePositions = [
		{ value: PAYMENT_CONFERENCE_POSITION_PROFESSOR, label: t('payment.modal.memberPosition.professor') },
		{ value: PAYMENT_CONFERENCE_POSITION_GRADUATE, label: t('payment.modal.memberPosition.graduate') },
		{ value: PAYMENT_CONFERENCE_POSITION_UNDERGRADUATE, label: t('payment.modal.memberPosition.undergraduate') },
	];
	const annualFeeMembers = [
		{ value: PAYMENT_MEMBER_REGULAR, disabled: false },
		{ value: PAYMENT_MEMBER_STUDENT, disabled: false },
		{ value: PAYMENT_MEMBER_LIFETIME, disabled: true },
		{ value: PAYMENT_MEMBER_BOARD, disabled: true },
	];
	const defaultMemberType =
		memberType && PAYMENT_MEMBER_TYPES.includes(memberType as (typeof PAYMENT_MEMBER_TYPES)[number]) ? memberType : PAYMENT_MEMBER_REGULAR;

	const { register, handleSubmit, watch, setValue } = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			payment: PAYMENT_METHOD_ACCOUNT,
			member: defaultMemberType,
			memberPosition: PAYMENT_CONFERENCE_POSITION_PROFESSOR,
			publicationType: PAYMENT_PUBLICATION_TYPE_BASIC,
		},
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const isSubmittingRef = useRef(false);
	const { mutateAsync: paymentReady, isPending: isPaymentReadyPending } = usePostPaymentReadyApi();
	const { mutateAsync: createTransferInfo, isPending: isTransferPending } = usePostCreateTransferInfoApi();
	const isPaymentProcessing = isSubmitting || isPaymentReadyPending || isTransferPending;

	const getAmount = () => {
		if (isAnnualFee) {
			return convertPaymentValue(watch('member'));
		}
		if (isConferenceFee) {
			return convertConferencePaymentValue(watch('memberPosition'));
		}
		return convertPublicationPaymentValue(watch('publicationType'));
	};

	const getGoodsName = () => {
		if (goodsName === PAYMENT_GOODS_ANNUAL_FEE) {
			return `${year}${PAYMENT_ANNUAL_FEE_YEAR_SUFFIX} ${PAYMENT_GOODS_ANNUAL_FEE}`;
		}
		if (goodsName === PAYMENT_GOODS_CONFERENCE_FEE) {
			return CONF_TITLE;
		}
		return `${PAYMENT_GOODS_PUBLICATION_FEE}(${watch('publicationType')})`;
	};

	const getMemberType = () => (isAnnualFee ? watch('member') || PAYMENT_MEMBER_REGULAR : (memberType ?? watch('member') ?? ''));
	const getConferenceMemberPosition = () => (isConferenceFee ? watch('memberPosition') : '');
	const createPaymentPayload = () => ({
		...(isConferenceFee ? {} : { member_type: getMemberType() || undefined }),
		...(isAnnualFee ? {} : { member_position: getConferenceMemberPosition() || undefined }),
		amount: getAmount().replace(/,/g, ''),
		goods_type: goodsName,
		goods_name: getGoodsName(),
	});

	const handleModalClick = (payment: string) => {
		setValue('payment', payment);
	};

	const onSubmit = async (_: RegisterField) => {
		if (isOpen) {
			closeModal(false);
			return;
		}

		if (isSubmittingRef.current) {
			return;
		}

		isSubmittingRef.current = true;
		setIsSubmitting(true);
		try {
			if (watch('payment') === PAYMENT_METHOD_ACCOUNT) {
				const res = await createTransferInfo(createPaymentPayload());
				if (res.data?.success) {
					setIsOpen(true);
				}
				return;
			}

			if (watch('payment') === PAYMENT_METHOD_CARD) {
				await paymentReady({
					...createPaymentPayload(),
					amount: Number(getAmount().replace(/,/g, '')),
				});
			}
		} finally {
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	return (
		<PaymentContainer onSubmit={handleSubmit(onSubmit)}>
			<Title>
				{isOpen
					? t('payment.modal.accountTransferTitle')
					: goodsName === PAYMENT_GOODS_ANNUAL_FEE
						? t('payment.modal.annualFeeTitle', { year })
						: goodsName === PAYMENT_GOODS_CONFERENCE_FEE
							? t('payment.modal.conferenceFeeTitle')
							: t('payment.modal.articleFeeTitle')}
			</Title>

			{!isOpen && (
				<>
					{isAnnualFee ? (
						<>
							<Field title="payment.modal.memberType" gap="0.8rem">
								<GenderBox>
									{annualFeeMembers.map((member) => (
										<GenderRadio key={member.value} $disabled={member.disabled}>
											<input type="radio" value={member.value} disabled={member.disabled} {...register('member')} />
											{t(member.value)}
										</GenderRadio>
									))}
								</GenderBox>
							</Field>
							<AmountBox>
								<div>{t('payment.modal.amount')}</div>
								<div className="point">
									<strong>{getAmount()}</strong> {t('common.currency.krw')}
								</div>
							</AmountBox>
						</>
					) : isConferenceFee ? (
						<>
							<Field title="payment.modal.memberPosition.title" gap="0.8rem">
								<GenderBox>
									{conferencePositions.map((position) => (
										<GenderRadio key={position.value}>
											<input type="radio" value={position.value} {...register('memberPosition')} />
											{position.label}
										</GenderRadio>
									))}
								</GenderBox>
							</Field>
							<AmountBox>
								<div>{t('payment.modal.amount')}</div>
								<div className="point">
									<strong>{getAmount()}</strong> {t('common.currency.krw')}
								</div>
							</AmountBox>
						</>
					) : (
						<AmountBox>
							<div>{t('payment.modal.amount')}</div>
							<div className="point">
								<strong>{getAmount()}</strong> {t('common.currency.krw')}
							</div>
						</AmountBox>
					)}
					{goodsName === PAYMENT_GOODS_PUBLICATION_FEE && (
						<Field title="payment.modal.publicationType" gap="0.8rem">
							<GenderBox>
								<GenderRadio>
									<input type="radio" value={PAYMENT_PUBLICATION_TYPE_BASIC} {...register('publicationType')} />
									{t('payment.modal.basic')}
								</GenderRadio>
								<GenderRadio>
									<input type="radio" value={PAYMENT_PUBLICATION_TYPE_EXPRESS} {...register('publicationType')} />
									{t('payment.modal.express')}
								</GenderRadio>
							</GenderBox>
						</Field>
					)}

					<Field title="payment.modal.method" gap="0.8rem">
						<PaymentContetnBox>
							<PaymentContent className={watch('payment') === PAYMENT_METHOD_ACCOUNT ? 'active' : ''} onClick={() => handleModalClick(PAYMENT_METHOD_ACCOUNT)}>
								{watch('payment') === PAYMENT_METHOD_ACCOUNT ? <MoneyIconActive /> : <MoneyIcon />} {t('payment.modal.account')}
							</PaymentContent>
						</PaymentContetnBox>
					</Field>
				</>
			)}
			{isOpen && (
				<PaymentAccountBox>
					{isAnnualFee && (
						<div className="item">
							<p className="title">{t('payment.modal.memberType')}</p>
							<p className="content point">
								<p className="content">{t(getMemberType())}</p>
							</p>
						</div>
					)}
					{isConferenceFee && (
						<div className="item">
							<p className="title">{t('payment.modal.memberPosition.title')}</p>
							<p className="content">{conferencePositions.find((position) => position.value === watch('memberPosition'))?.label ?? watch('memberPosition')}</p>
						</div>
					)}
					{goodsName === PAYMENT_GOODS_PUBLICATION_FEE && (
						<div className="item">
							<p className="title">{t('payment.modal.publicationType')}</p>
							<p className="content">{watch('publicationType') === PAYMENT_PUBLICATION_TYPE_EXPRESS ? t('payment.modal.express') : t('payment.modal.basic')}</p>
						</div>
					)}

					<div className="item">
						<p className="title">{t('payment.modal.depositAmount')}</p>
						<p className="content point">
							<strong className="point_bold">{getAmount()}</strong>
							{t('common.currency.krw')}
						</p>
					</div>
					<div className="item">
						<p className="title">{t('payment.modal.bankAccount')}</p>
						<p className="content">
							{t('payment.modal.bankName')}&nbsp;
							<strong className="point_bold">301-0122-6751-81</strong>
						</p>
					</div>
					<div className="item">
						<p className="title">{t('payment.modal.depositor')}</p>
						<p className="content">{t('payment.modal.depositorName')}</p>
					</div>
				</PaymentAccountBox>
			)}

			<PaymentButtonBox>
				{!isOpen && (
					<CancelButton type="button" onClick={() => closeModal(false)}>
						{t('common.actions.cancel')}
					</CancelButton>
				)}
				<PaymentConfirmButton type="submit" disabled={isPaymentProcessing}>
					{!isOpen ? t('common.actions.pay') : t('common.actions.confirm')}
				</PaymentConfirmButton>
			</PaymentButtonBox>
		</PaymentContainer>
	);
};

export default PaymentModal;

const PaymentContainer = styled.form`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 2rem;
`;
const Title = styled.h2`
	font-size: ${(props) => props.theme.fontSize.sizeXl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	color: #212529;
`;
const PaymentTitle = styled.div`
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	&.top {
		margin-top: 2rem;
	}
`;
const AmountBox = styled(PaymentTitle)`
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	.point {
		color: ${(props) => props.theme.colors.primary400};
		font-size: ${(props) => props.theme.fontSize.sizeLg};
		font-weight: ${(props) => props.theme.fontWeight.weightRegular};
		strong {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
		}
	}
`;
const PaymentButtonBox = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	gap: 1.2rem;
`;

const PaymentContetnBox = styled.div`
	display: flex;
	gap: 0.8rem;
	width: 100%;
`;
const PaymentContent = styled.div`
	display: flex;
	height: 7.2rem;
	align-items: center;
	justify-content: center;
	width: 100%;
	gap: 0.8rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	color: #868e96;
	background-color: #fff;
	padding: 0rem 1.2rem 0rem 0.8rem;
	border: 1px solid #dee2e6;
	cursor: pointer;
	&.active {
		border: 2px solid ${(props) => props.theme.colors.primary400};
		color: ${(props) => props.theme.colors.primary400};
	}
`;
const GenderBox = styled.div`
	display: flex;
	gap: 4rem;
`;
const GenderRadio = styled.div<{ $disabled?: boolean }>`
	display: flex;
	gap: 0.4rem;
	align-items: center;
	color: #212529;
	${(props) =>
		props.$disabled &&
		`
		color: #868e96;
	`}
	input[type='radio'] {
		-webkit-appearance: none;
		-moz-appearance: none;
		appearance: none;
		width: 2rem;
		height: 2rem;
		border: 1px solid #ced4da;
		border-radius: 50%;
		outline: none;
		cursor: pointer;
	}
	input[type='radio']:disabled {
		cursor: not-allowed;
		background-color: #f1f3f5;
	}
	input[type='radio']:checked {
		background-color: #fff;
		border: 6px solid ${(props) => props.theme.colors.primary400};
	}
	input[type='radio']:checked:disabled {
		background-color: #fff;
		border: 6px solid ${(props) => props.theme.colors.primary400};
	}
`;
const PaymentAccountBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	background-color: #fff;
	.item {
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
		.title {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
			line-height: ${(props) => props.theme.ui.lineHeight.heading};
			letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
			color: #212529;
		}
		.content {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightRegular};
			line-height: ${(props) => props.theme.ui.lineHeight.heading};
			letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
			color: #212529;
		}
		.point_bold {
			color: ${(props) => props.theme.colors.primary400};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
		}
		.point {
			color: ${(props) => props.theme.colors.primary400};
		}
	}
`;
